import React, { useState, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Grid, Box, Radio, RadioGroup, FormControlLabel } from '@material-ui/core'
import clsx from 'clsx'
import SydInput from '../../../../commonDesign/SydInput'
import SydTextArea from '../../../../commonDesign/SydTextArea'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import SydLabel from '../../../../commonDesign/SydLabel'
import SaveCancelButtons from '../../../../molecules/SaveCancelButtons'
import WYSIWYGEditor from '../../../../organisms/WYSIWYGEditor'
import Icon from '../../../../atoms/Icon'
import { useInsertAiSkill } from '../../../../../api/denaliAssistant'
import IconPicker from './IconPicker'

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    overflowY: 'auto'
  },
  errorMessage: {
    color: 'var(--color-danger-500)',
    fontStyle: 'italic',
    marginBottom: theme.spacing(1)
  },
  charCount: {
    fontSize: '12px',
    color: 'var(--dai-color-text-low)',
    textAlign: 'right',
    marginTop: theme.spacing(0.5)
  },
  charCountWarning: {
    color: 'var(--color-warning-500)'
  },
  buttonContainer: {
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end'
  }
}))

const FOCUS_OPTIONS = [
  { value: 'any', label: 'any' },
  { value: 'none', label: 'none' },
  { value: 'client', label: 'client' }
]

const DEFAULT_ICON = 'cube'

function SkillForm ({ skill, onCancel, onSuccess }) {
  const classes = useStyles()
  const insertMutation = useInsertAiSkill()
  const [error, setError] = useState(null)
  const [iconPickerOpen, setIconPickerOpen] = useState(false)
  const isNewMode = useMemo(() => !skill, [skill])

  const initialFormData = useMemo(() => {
    if (isNewMode) {
      return {
        skillId: '',
        skillName: '',
        enabled: false,
        description: '',
        defaultPrompt: '',
        focuses: 'any',
        useWhen: '',
        dontUseWhen: '',
        instructions: '',
        iconName: DEFAULT_ICON
      }
    }

    const content = skill.skillContent || {}
    const enabled = Boolean(content.enabled) || Boolean(skill.enabled)

    return {
      skillId: skill.skillId,
      skillName: skill.skillName,
      enabled,
      description: content.description || '',
      defaultPrompt: content.defaultPrompt || '',
      focuses: Array.isArray(content.focuses) && content.focuses.length > 0 ? content.focuses[0] : 'any',
      useWhen: Array.isArray(content.useWhen) ? content.useWhen.join('\n') : '',
      dontUseWhen: Array.isArray(content.dontUseWhen) ? content.dontUseWhen.join('\n') : '',
      instructions: content.instructions || '',
      iconName: content.iconName || DEFAULT_ICON
    }
  }, [skill, isNewMode])

  const [formData, setFormData] = useState(initialFormData)
  const [validationErrors, setValidationErrors] = useState({})

  useEffect(() => {
    setFormData(initialFormData)
    setError(null)
    setValidationErrors({})
  }, [initialFormData])

  const processLines = useCallback((text) => text.split('\n').map(l => l.trim()).filter(Boolean), [])

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
    setValidationErrors(prev => {
      const { [field]: removed, ...rest } = prev
      return removed ? rest : prev
    })
  }, [])

  // Memoized field update handlers
  const handleSkillIdChange = useCallback((e) => updateField('skillId', e.target.value), [updateField])
  const handleSkillNameChange = useCallback((e) => updateField('skillName', e.target.value), [updateField])
  const handleEnabledChange = useCallback((e, checked) => updateField('enabled', checked), [updateField])
  const handleDescriptionChange = useCallback((e) => updateField('description', e.target.value), [updateField])
  const handleDefaultPromptChange = useCallback((e) => updateField('defaultPrompt', e.target.value), [updateField])
  const handleFocusesChange = useCallback((e) => updateField('focuses', e.target.value), [updateField])
  const handleUseWhenChange = useCallback((e) => updateField('useWhen', e.target.value), [updateField])
  const handleDontUseWhenChange = useCallback((e) => updateField('dontUseWhen', e.target.value), [updateField])
  const handleInstructionsChange = useCallback((value) => updateField('instructions', value), [updateField])
  const handleIconNameChange = useCallback((iconName) => updateField('iconName', iconName), [updateField])

  const validateForm = useCallback(() => {
    const errors = {}

    if (isNewMode) {
      if (!formData.skillId.trim()) {
        errors.skillId = 'Skill ID is required'
      } else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(formData.skillId)) {
        errors.skillId = 'Skill ID must be lowercase-snake-case alphanumeric (e.g., my-skill-1)'
      } else if (formData.skillId.length > 64) {
        errors.skillId = 'Skill ID must be 64 characters or less'
      }
    }

    if (!formData.skillName.trim()) {
      errors.skillName = 'Skill Name is required'
    } else if (formData.skillName.length > 256) {
      errors.skillName = 'Skill Name must be 256 characters or less'
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required'
    } else if (formData.description.length > 200) {
      errors.description = 'Description must be 200 characters or less'
    }

    const useWhenLines = processLines(formData.useWhen)
    if (useWhenLines.length === 0) {
      errors.useWhen = 'Use When must have at least one entry'
    }
    if (formData.useWhen.length > 500) {
      errors.useWhen = 'Use When must be 500 characters or less'
    }

    if (formData.dontUseWhen.length > 500) {
      errors.dontUseWhen = 'Don\'t Use When must be 500 characters or less'
    }

    if (!formData.instructions.trim()) {
      errors.instructions = 'Instructions is required'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData, isNewMode, processLines])

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return
    }

    setError(null)

    try {
      const useWhenLines = processLines(formData.useWhen)
      const dontUseWhenLines = processLines(formData.dontUseWhen)

      const skillContent = {
        enabled: formData.enabled,
        focuses: [formData.focuses],
        useWhen: useWhenLines,
        dontUseWhen: dontUseWhenLines,
        description: formData.description.trim(),
        defaultPrompt: formData.defaultPrompt,
        instructions: formData.instructions.trim(),
        iconName: formData.iconName || DEFAULT_ICON
      }

      const result = await insertMutation.mutateAsync({
        skillId: formData.skillId,
        skillName: formData.skillName.trim(),
        skillContent
      })

      if (result?.skill) {
        onSuccess(result.skill)
      }
    } catch (err) {
      console.error('Error saving skill:', err)
      setError(err?.response?.data?.detail || err?.message || 'An error occurred while saving')
    }
  }, [formData, validateForm, insertMutation, onSuccess, processLines])

  const handleCancel = useCallback(() => {
    setFormData(initialFormData)
    setError(null)
    setValidationErrors({})
    onCancel()
  }, [initialFormData, onCancel])

  const hasErrors = Boolean(error || Object.keys(validationErrors).length)

  return (
    <div className={classes.form}>
      {hasErrors && (
        <Box className={classes.errorMessage}>
          {error || 'Please fix validation errors'}
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <SydLabel label='Skill ID' required={isNewMode} error={validationErrors.skillId}>
            <SydInput
              value={formData.skillId}
              onChange={isNewMode ? handleSkillIdChange : undefined}
              disabled={!isNewMode}
              placeholder={isNewMode ? 'e.g., my-skill-1' : undefined}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={12} md={4}>
          <SydLabel label='Skill Name' required error={validationErrors.skillName}>
            <SydInput
              value={formData.skillName}
              onChange={handleSkillNameChange}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={12} md={1}>
          <Box display='flex' alignItems='center' gap={1}>
            <SydLabel label='Icon'>
              <Box
                display='flex'
                alignItems='center'
                style={{ cursor: 'pointer', width: 'fit-content' }}
                onClick={() => setIconPickerOpen(true)}
              >
                <Icon name={formData.iconName || DEFAULT_ICON} customSize='32px' />
              </Box>
            </SydLabel>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <SydLabel label='Focuses' required>
            <RadioGroup
              value={formData.focuses}
              onChange={handleFocusesChange}
              row
            >
              {FOCUS_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size='small' />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </SydLabel>
        </Grid>
        <Grid item xs={12} md={1}>
          <Box display='flex' alignItems='center' gap={1}>
            <SydLabel label='Enabled'>
              <Box display='flex' alignItems='center' height='100%'>
                <SydCheckbox
                  value={formData.enabled}
                  onChange={handleEnabledChange}
                />
              </Box>
            </SydLabel>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <SydLabel label='Description' required error={validationErrors.description}>
            <SydTextArea
              value={formData.description}
              onChange={handleDescriptionChange}
              inputProps={{ maxLength: 200 }}
            />
            <div className={clsx(classes.charCount, { [classes.charCountWarning]: formData.description.length > 180 })}>
              {formData.description.length}/200
            </div>
          </SydLabel>
        </Grid>
        <Grid item xs={12} md={6}>
          <SydLabel label='Default Prompt' error={validationErrors.defaultPrompt}>
            <SydTextArea
              value={formData.defaultPrompt}
              onChange={handleDefaultPromptChange}
              inputProps={{ maxLength: 300 }}
            />
            <div className={clsx(classes.charCount, { [classes.charCountWarning]: formData.defaultPrompt.length > 270 })}>
              {formData.defaultPrompt.length}/300
            </div>
          </SydLabel>
        </Grid>
        <Grid item xs={12} md={6}>
          <SydLabel label='Use When' required error={validationErrors.useWhen}>
            <SydTextArea
              value={formData.useWhen}
              onChange={handleUseWhenChange}
              placeholder='Enter one sentence per line'
              inputProps={{ maxLength: 500 }}
            />
            <div className={clsx(classes.charCount, { [classes.charCountWarning]: formData.useWhen.length > 450 })}>
              {formData.useWhen.length}/500
            </div>
          </SydLabel>
        </Grid>
        <Grid item xs={12} md={6}>
          <SydLabel label="Don't Use When" error={validationErrors.dontUseWhen}>
            <SydTextArea
              value={formData.dontUseWhen}
              onChange={handleDontUseWhenChange}
              placeholder='Enter one sentence per line'
              inputProps={{ maxLength: 500 }}
            />
            <div className={clsx(classes.charCount, { [classes.charCountWarning]: formData.dontUseWhen.length > 450 })}>
              {formData.dontUseWhen.length}/500
            </div>
          </SydLabel>
        </Grid>
        <Grid item xs={12}>
          <SydLabel
            label='Instructions'
            required
            error={validationErrors.instructions}
          >
            <WYSIWYGEditor
              defaultValue={formData.instructions}
              onChange={handleInstructionsChange}
              placeholder='Start typing your instructions here...'
              isMarkdown
            />
          </SydLabel>
        </Grid>
      </Grid>

      <div className={classes.buttonContainer}>
        <SaveCancelButtons
          onSave={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={insertMutation.isLoading}
          isFormValid={Object.keys(validationErrors).length === 0}
        />
      </div>

      <IconPicker
        open={iconPickerOpen}
        onClose={() => setIconPickerOpen(false)}
        selectedIcon={formData.iconName}
        onSelect={handleIconNameChange}
      />
    </div>
  )
}

SkillForm.propTypes = {
  skill: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
}

export default SkillForm
